<script setup>
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { getRandomColor } from 'dashboard/helper/labelColor';
import Draggable from 'vuedraggable';

import NextButton from 'dashboard/components-next/button/Button.vue';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const funnelId = computed(() => Number(route.params.funnelId));

const funnel = useMapGetter('funnels/getFunnel');
const teams = useMapGetter('teams/getTeams');

const currentFunnel = computed(() => funnel.value(funnelId.value));

const name = ref('');
const description = ref('');
const selectedTeamIds = ref([]);
const stages = ref([]);
const newStageName = ref('');
const newStageColor = ref(getRandomColor());
const isSaving = ref(false);

const showStageDeletePopup = ref(false);
const selectedStage = ref({});

const FIELD_TYPES = ['text', 'number', 'date', 'list'];

// Static i18n keys per type (avoids dynamic i18n keys in the template).
const fieldTypeLabels = computed(() => ({
  text: t('FUNNELS.SETTINGS.FIELD.TYPES.TEXT'),
  number: t('FUNNELS.SETTINGS.FIELD.TYPES.NUMBER'),
  date: t('FUNNELS.SETTINGS.FIELD.TYPES.DATE'),
  list: t('FUNNELS.SETTINGS.FIELD.TYPES.LIST'),
}));

const fields = ref([]);
const newFieldName = ref('');
const newFieldType = ref('text');
const newFieldRequired = ref(false);
const newFieldOptions = ref([]);
const newOptionValue = ref('');
// Per-field "add option" input, keyed by field id (list fields only).
const fieldOptionInputs = ref({});

const showFieldDeletePopup = ref(false);
const selectedField = ref({});

// Local, editable snapshot of the funnel so drag/rename don't mutate the store
// directly (Vuex strict mode). Re-synced whenever the funnel changes in the store.
const syncFromStore = () => {
  const record = currentFunnel.value;
  if (!record) return;
  name.value = record.name || '';
  description.value = record.description || '';
  selectedTeamIds.value = [...(record.team_ids || [])];
  stages.value = [...(record.stages || [])]
    .sort((a, b) => a.position - b.position)
    .map(stage => ({ ...stage }));
  fields.value = [...(record.fields || [])]
    .sort((a, b) => a.position - b.position)
    .map(field => ({ ...field, options: [...(field.options || [])] }));
};

watch(currentFunnel, syncFromStore, { immediate: true });

const toggleTeam = teamId => {
  selectedTeamIds.value = selectedTeamIds.value.includes(teamId)
    ? selectedTeamIds.value.filter(id => id !== teamId)
    : [...selectedTeamIds.value, teamId];
};

const goBack = () => {
  router.push({ name: 'settings_funnels_index', params: route.params });
};

const saveDetails = async () => {
  if (!name.value.trim()) return;
  isSaving.value = true;
  try {
    await store.dispatch('funnels/updateFunnel', {
      id: funnelId.value,
      name: name.value.trim(),
      description: description.value.trim(),
      teamIds: selectedTeamIds.value,
    });
    useAlert(t('FUNNELS.SETTINGS.UPDATE.SUCCESS'));
  } catch (error) {
    useAlert(t('FUNNELS.SETTINGS.UPDATE.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const addStage = async () => {
  const stageName = newStageName.value.trim();
  if (!stageName) return;
  try {
    await store.dispatch('funnels/createStage', {
      funnelId: funnelId.value,
      name: stageName,
      color: newStageColor.value,
    });
    newStageName.value = '';
    newStageColor.value = getRandomColor();
    useAlert(t('FUNNELS.SETTINGS.STAGE.CREATE_SUCCESS'));
  } catch (error) {
    useAlert(t('FUNNELS.SETTINGS.STAGE.CREATE_ERROR'));
  }
};

const renameStage = async stage => {
  const stageName = stage.name.trim();
  const stored = (currentFunnel.value?.stages || []).find(
    item => item.id === stage.id
  );
  if (!stageName || stored?.name === stageName) return;
  try {
    await store.dispatch('funnels/updateStage', {
      funnelId: funnelId.value,
      stageId: stage.id,
      name: stageName,
    });
    useAlert(t('FUNNELS.SETTINGS.STAGE.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('FUNNELS.SETTINGS.STAGE.UPDATE_ERROR'));
  }
};

// The color picker emits continuously while dragging inside the palette, so
// update the local swatch live and debounce the persist to a single request.
let colorSaveTimer = null;
const changeStageColor = (stage, color) => {
  stage.color = color;
  clearTimeout(colorSaveTimer);
  colorSaveTimer = setTimeout(async () => {
    try {
      await store.dispatch('funnels/updateStage', {
        funnelId: funnelId.value,
        stageId: stage.id,
        name: stage.name.trim(),
        color,
      });
    } catch (error) {
      useAlert(t('FUNNELS.SETTINGS.STAGE.UPDATE_ERROR'));
    }
  }, 500);
};

const openStageDeletePopup = stage => {
  selectedStage.value = stage;
  showStageDeletePopup.value = true;
};
const closeStageDeletePopup = () => {
  showStageDeletePopup.value = false;
};

const confirmStageDeletion = async () => {
  closeStageDeletePopup();
  try {
    await store.dispatch('funnels/deleteStage', {
      funnelId: funnelId.value,
      stageId: selectedStage.value.id,
    });
    useAlert(t('FUNNELS.SETTINGS.STAGE.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('FUNNELS.SETTINGS.STAGE.DELETE_ERROR'));
  }
};

const onReorderEnd = async () => {
  try {
    await store.dispatch('funnels/reorderStages', {
      funnelId: funnelId.value,
      stageIds: stages.value.map(stage => stage.id),
    });
  } catch (error) {
    useAlert(t('FUNNELS.SETTINGS.STAGE.REORDER_ERROR'));
    syncFromStore();
  }
};

// --- Custom fields ---
const addNewOption = () => {
  const value = newOptionValue.value.trim();
  if (!value || newFieldOptions.value.includes(value)) return;
  newFieldOptions.value = [...newFieldOptions.value, value];
  newOptionValue.value = '';
};

const removeNewOption = option => {
  newFieldOptions.value = newFieldOptions.value.filter(item => item !== option);
};

const resetNewField = () => {
  newFieldName.value = '';
  newFieldType.value = 'text';
  newFieldRequired.value = false;
  newFieldOptions.value = [];
  newOptionValue.value = '';
};

const canAddField = computed(() => {
  if (!newFieldName.value.trim()) return false;
  if (newFieldType.value === 'list' && !newFieldOptions.value.length) {
    return false;
  }
  return true;
});

const addField = async () => {
  if (!canAddField.value) return;
  const payload = {
    funnelId: funnelId.value,
    name: newFieldName.value.trim(),
    field_type: newFieldType.value,
    required: newFieldRequired.value,
  };
  if (newFieldType.value === 'list') payload.options = newFieldOptions.value;
  try {
    await store.dispatch('funnels/createField', payload);
    resetNewField();
    useAlert(t('FUNNELS.SETTINGS.FIELD.CREATE_SUCCESS'));
  } catch (error) {
    useAlert(t('FUNNELS.SETTINGS.FIELD.CREATE_ERROR'));
  }
};

const renameField = async field => {
  const fieldName = field.name.trim();
  const stored = (currentFunnel.value?.fields || []).find(
    item => item.id === field.id
  );
  if (!fieldName || stored?.name === fieldName) return;
  try {
    await store.dispatch('funnels/updateField', {
      funnelId: funnelId.value,
      fieldId: field.id,
      name: fieldName,
    });
    useAlert(t('FUNNELS.SETTINGS.FIELD.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('FUNNELS.SETTINGS.FIELD.UPDATE_ERROR'));
    syncFromStore();
  }
};

const toggleFieldRequired = async field => {
  const required = !field.required;
  field.required = required;
  try {
    await store.dispatch('funnels/updateField', {
      funnelId: funnelId.value,
      fieldId: field.id,
      required,
    });
  } catch (error) {
    useAlert(t('FUNNELS.SETTINGS.FIELD.UPDATE_ERROR'));
    syncFromStore();
  }
};

const saveFieldOptions = async (field, options) => {
  try {
    await store.dispatch('funnels/updateField', {
      funnelId: funnelId.value,
      fieldId: field.id,
      options,
    });
  } catch (error) {
    useAlert(t('FUNNELS.SETTINGS.FIELD.UPDATE_ERROR'));
    syncFromStore();
  }
};

const addOptionToField = field => {
  const value = (fieldOptionInputs.value[field.id] || '').trim();
  if (!value || (field.options || []).includes(value)) return;
  const options = [...(field.options || []), value];
  field.options = options;
  fieldOptionInputs.value[field.id] = '';
  saveFieldOptions(field, options);
};

const removeOptionFromField = (field, option) => {
  const options = (field.options || []).filter(item => item !== option);
  if (!options.length) {
    useAlert(t('FUNNELS.SETTINGS.FIELD.OPTIONS_REQUIRED'));
    return;
  }
  field.options = options;
  saveFieldOptions(field, options);
};

const openFieldDeletePopup = field => {
  selectedField.value = field;
  showFieldDeletePopup.value = true;
};
const closeFieldDeletePopup = () => {
  showFieldDeletePopup.value = false;
};

const confirmFieldDeletion = async () => {
  closeFieldDeletePopup();
  try {
    await store.dispatch('funnels/deleteField', {
      funnelId: funnelId.value,
      fieldId: selectedField.value.id,
    });
    useAlert(t('FUNNELS.SETTINGS.FIELD.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('FUNNELS.SETTINGS.FIELD.DELETE_ERROR'));
  }
};

const onFieldReorderEnd = async () => {
  try {
    await store.dispatch('funnels/reorderFields', {
      funnelId: funnelId.value,
      fieldIds: fields.value.map(field => field.id),
    });
  } catch (error) {
    useAlert(t('FUNNELS.SETTINGS.FIELD.REORDER_ERROR'));
    syncFromStore();
  }
};

onBeforeMount(() => {
  store.dispatch('funnels/fetchFunnels');
  store.dispatch('teams/get');
});
</script>

<template>
  <div class="flex flex-col w-full gap-6">
    <div class="flex items-center gap-2">
      <NextButton
        icon="i-lucide-arrow-left"
        slate
        faded
        sm
        :label="t('FUNNELS.SETTINGS.DETAIL.BACK')"
        @click="goBack"
      />
    </div>

    <div v-if="currentFunnel" class="flex flex-col gap-8">
      <!-- Details + visibility -->
      <section class="flex flex-col gap-4">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('FUNNELS.SETTINGS.DETAIL.DETAILS_TITLE') }}
        </h2>
        <woot-input
          v-model="name"
          class="w-full"
          :label="t('FUNNELS.SETTINGS.FORM.NAME.LABEL')"
          :placeholder="t('FUNNELS.SETTINGS.FORM.NAME.PLACEHOLDER')"
        />
        <woot-input
          v-model="description"
          class="w-full"
          :label="t('FUNNELS.SETTINGS.FORM.DESCRIPTION.LABEL')"
          :placeholder="t('FUNNELS.SETTINGS.FORM.DESCRIPTION.PLACEHOLDER')"
        />

        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('FUNNELS.SETTINGS.DETAIL.TEAMS_TITLE') }}
          </span>
          <p class="text-sm text-n-slate-11">
            {{ t('FUNNELS.SETTINGS.DETAIL.TEAMS_HELP') }}
          </p>
          <div
            v-if="teams.length"
            class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak"
          >
            <label
              v-for="team in teams"
              :key="team.id"
              class="flex items-center gap-2 text-sm text-n-slate-12"
            >
              <input
                type="checkbox"
                :checked="selectedTeamIds.includes(team.id)"
                @change="toggleTeam(team.id)"
              />
              {{ team.name }}
            </label>
          </div>
          <p v-else class="text-sm text-n-slate-10">
            {{ t('FUNNELS.SETTINGS.DETAIL.NO_TEAMS') }}
          </p>
        </div>

        <div class="flex justify-end">
          <NextButton
            :label="t('FUNNELS.SETTINGS.FORM.SAVE')"
            :disabled="!name.trim() || isSaving"
            :is-loading="isSaving"
            @click="saveDetails"
          />
        </div>
      </section>

      <!-- Stages -->
      <section class="flex flex-col gap-4">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('FUNNELS.SETTINGS.DETAIL.STAGES_TITLE') }}
        </h2>

        <Draggable
          :list="stages"
          item-key="id"
          handle=".stage-drag-handle"
          ghost-class="opacity-50"
          class="flex flex-col gap-2"
          @end="onReorderEnd"
        >
          <template #item="{ element }">
            <div
              class="flex items-center gap-2 p-2 border rounded-lg border-n-weak"
            >
              <span
                class="stage-drag-handle i-lucide-grip-vertical size-4 text-n-slate-10 cursor-grab shrink-0"
              />
              <ColorPicker
                :model-value="element.color"
                @update:model-value="color => changeStageColor(element, color)"
              />
              <woot-input
                v-model="element.name"
                class="flex-1 mb-0"
                @keyup.enter="renameStage(element)"
                @blur="renameStage(element)"
              />
              <NextButton
                v-tooltip.top="t('FUNNELS.SETTINGS.STAGE.DELETE')"
                icon="i-woot-bin"
                slate
                sm
                ghost
                class="hover:enabled:text-n-ruby-11"
                @click="openStageDeletePopup(element)"
              />
            </div>
          </template>
        </Draggable>

        <p v-if="!stages.length" class="text-sm text-n-slate-10">
          {{ t('FUNNELS.SETTINGS.STAGE.EMPTY') }}
        </p>

        <form class="flex items-center gap-2" @submit.prevent="addStage">
          <ColorPicker v-model="newStageColor" />
          <woot-input
            v-model="newStageName"
            class="flex-1 mb-0"
            :placeholder="t('FUNNELS.SETTINGS.STAGE.ADD_PLACEHOLDER')"
          />
          <NextButton
            type="submit"
            :label="t('FUNNELS.SETTINGS.STAGE.ADD')"
            :disabled="!newStageName.trim()"
          />
        </form>
      </section>

      <!-- Custom fields -->
      <section class="flex flex-col gap-4">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('FUNNELS.SETTINGS.FIELD.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('FUNNELS.SETTINGS.FIELD.HELP') }}
        </p>

        <Draggable
          :list="fields"
          item-key="id"
          handle=".field-drag-handle"
          ghost-class="opacity-50"
          class="flex flex-col gap-2"
          @end="onFieldReorderEnd"
        >
          <template #item="{ element }">
            <div
              class="flex flex-col gap-2 p-2 border rounded-lg border-n-weak"
            >
              <div class="flex items-center gap-2">
                <span
                  class="field-drag-handle i-lucide-grip-vertical size-4 text-n-slate-10 cursor-grab shrink-0"
                />
                <woot-input
                  v-model="element.name"
                  class="flex-1 mb-0"
                  @keyup.enter="renameField(element)"
                  @blur="renameField(element)"
                />
                <span
                  class="px-2 py-1 text-xs rounded-md shrink-0 bg-n-alpha-2 text-n-slate-11"
                >
                  {{ fieldTypeLabels[element.field_type] }}
                </span>
                <label
                  class="flex items-center gap-1 text-xs text-n-slate-11 shrink-0"
                >
                  <input
                    type="checkbox"
                    :checked="element.required"
                    @change="toggleFieldRequired(element)"
                  />
                  {{ t('FUNNELS.SETTINGS.FIELD.REQUIRED') }}
                </label>
                <NextButton
                  v-tooltip.top="t('FUNNELS.SETTINGS.FIELD.DELETE')"
                  icon="i-woot-bin"
                  slate
                  sm
                  ghost
                  class="hover:enabled:text-n-ruby-11"
                  @click="openFieldDeletePopup(element)"
                />
              </div>

              <div
                v-if="element.field_type === 'list'"
                class="flex flex-col gap-2 pl-6"
              >
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="option in element.options"
                    :key="option"
                    class="flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-n-alpha-2 text-n-slate-12"
                  >
                    {{ option }}
                    <button
                      type="button"
                      class="i-lucide-x size-3 text-n-slate-10 hover:text-n-ruby-11"
                      @click="removeOptionFromField(element, option)"
                    />
                  </span>
                </div>
                <div class="flex items-center gap-2">
                  <woot-input
                    v-model="fieldOptionInputs[element.id]"
                    class="flex-1 mb-0"
                    :placeholder="
                      t('FUNNELS.SETTINGS.FIELD.OPTION_PLACEHOLDER')
                    "
                    @keyup.enter="addOptionToField(element)"
                  />
                  <NextButton
                    faded
                    slate
                    sm
                    :label="t('FUNNELS.SETTINGS.FIELD.ADD_OPTION')"
                    :disabled="!(fieldOptionInputs[element.id] || '').trim()"
                    @click="addOptionToField(element)"
                  />
                </div>
              </div>
            </div>
          </template>
        </Draggable>

        <p v-if="!fields.length" class="text-sm text-n-slate-10">
          {{ t('FUNNELS.SETTINGS.FIELD.EMPTY') }}
        </p>

        <!-- Add field -->
        <form
          class="flex flex-col gap-3 p-3 border rounded-lg border-n-weak"
          @submit.prevent="addField"
        >
          <div class="flex items-center gap-2">
            <woot-input
              v-model="newFieldName"
              class="flex-1 mb-0"
              :placeholder="t('FUNNELS.SETTINGS.FIELD.ADD_PLACEHOLDER')"
            />
            <select
              v-model="newFieldType"
              class="w-32 h-10 px-2 text-sm border rounded-lg shrink-0 border-n-weak bg-n-alpha-black1 text-n-slate-12"
            >
              <option v-for="type in FIELD_TYPES" :key="type" :value="type">
                {{ fieldTypeLabels[type] }}
              </option>
            </select>
            <label
              class="flex items-center gap-1 text-sm shrink-0 text-n-slate-11"
            >
              <input v-model="newFieldRequired" type="checkbox" />
              {{ t('FUNNELS.SETTINGS.FIELD.REQUIRED') }}
            </label>
          </div>

          <div v-if="newFieldType === 'list'" class="flex flex-col gap-2">
            <div v-if="newFieldOptions.length" class="flex flex-wrap gap-1">
              <span
                v-for="option in newFieldOptions"
                :key="option"
                class="flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-n-alpha-2 text-n-slate-12"
              >
                {{ option }}
                <button
                  type="button"
                  class="i-lucide-x size-3 text-n-slate-10 hover:text-n-ruby-11"
                  @click="removeNewOption(option)"
                />
              </span>
            </div>
            <div class="flex items-center gap-2">
              <woot-input
                v-model="newOptionValue"
                class="flex-1 mb-0"
                :placeholder="t('FUNNELS.SETTINGS.FIELD.OPTION_PLACEHOLDER')"
                @keyup.enter.prevent="addNewOption"
              />
              <NextButton
                faded
                slate
                sm
                :label="t('FUNNELS.SETTINGS.FIELD.ADD_OPTION')"
                :disabled="!newOptionValue.trim()"
                @click="addNewOption"
              />
            </div>
          </div>

          <div class="flex justify-end">
            <NextButton
              type="submit"
              :label="t('FUNNELS.SETTINGS.FIELD.ADD')"
              :disabled="!canAddField"
            />
          </div>
        </form>
      </section>
    </div>

    <woot-delete-modal
      v-model:show="showStageDeletePopup"
      :on-close="closeStageDeletePopup"
      :on-confirm="confirmStageDeletion"
      :title="t('FUNNELS.SETTINGS.STAGE.DELETE_CONFIRM.TITLE')"
      :message="t('FUNNELS.SETTINGS.STAGE.DELETE_CONFIRM.MESSAGE')"
      :message-value="` ${selectedStage.name}?`"
      :confirm-text="t('FUNNELS.SETTINGS.STAGE.DELETE_CONFIRM.YES')"
      :reject-text="t('FUNNELS.SETTINGS.STAGE.DELETE_CONFIRM.NO')"
    />

    <woot-delete-modal
      v-model:show="showFieldDeletePopup"
      :on-close="closeFieldDeletePopup"
      :on-confirm="confirmFieldDeletion"
      :title="t('FUNNELS.SETTINGS.FIELD.DELETE_CONFIRM.TITLE')"
      :message="t('FUNNELS.SETTINGS.FIELD.DELETE_CONFIRM.MESSAGE')"
      :message-value="` ${selectedField.name}?`"
      :confirm-text="t('FUNNELS.SETTINGS.FIELD.DELETE_CONFIRM.YES')"
      :reject-text="t('FUNNELS.SETTINGS.FIELD.DELETE_CONFIRM.NO')"
    />
  </div>
</template>
